import { Box, Chip, CircularProgress, Divider, Fab, IconButton, Stack, Typography } from '@mui/material'
import { C1BorderRadius } from 'c1ui/theme/C1BorderRadius'
import { sidebarCollapsedWidth, sidebarWidth } from 'c1ui/theme/C1Spacing'
import { shadows } from 'c1ui/theme/C1Theme'
import { A2UISurfaceRenderer } from 'components/a2ui/A2UISurfaceRenderer'
import { SurfaceFeedback } from 'components/a2ui/SurfaceFeedback'
import { C1Dialog, C1DialogContent } from 'components/core/dialog/C1Dialog'
import { AgenticAiIcon } from 'components/core/icons/AgenticAiIcon'
import { C1Icon } from 'components/core/icons/C1Icon'
import { ChatInput } from 'components/pages/conversations/ChatInput'
import { ChatMessage } from 'components/pages/conversations/ChatMessage'
import { COLLAPSED_SIDEBAR_NAV_STORAGE_KEY } from 'components/pages/sidebar/CollapsingSidebar'
import { useLocalStorageContext } from 'hooks/local-storage-context'
import { useChatAssistant } from 'hooks/useChatAssistant'
import { SurfaceState } from 'lib/a2ui'
import { Maximize2, Minimize2, Minus, X } from 'lucide-react'
import { SurfaceLifecycleState } from 'pbts/c1/api/a2ui/v1/surface'
import { UIMessage } from 'pbts/c1/api/conversations/v1/conversations'
import { FC, ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { CopilotAssistantProps, TimelineItem } from './types'

const WINDOW_MARGIN = 16

/**
 * Unified chat assistant component for CEL, CSV, and Automation helpers.
 * Renders as a floating window at bottom-left that respects sidebar width.
 * Supports minimize (to FAB), expand (to dialog), and resize.
 */
export const CopilotAssistant: FC<CopilotAssistantProps> = ({
    conversationType,
    conversationTitle,
    metadata,
    renderActionItem,
    onLocalAction,
    zeroState,
    placeholder,
    title,
    onClose,
    showBetaChip = false,
    autoStartMessage,
    onError,
}) => {
    const intl = useIntl()
    const hasAutoStarted = useRef(false)

    // Track sidebar collapsed state for floating variant positioning
    const [isSidebarCollapsed] = useLocalStorageContext(COLLAPSED_SIDEBAR_NAV_STORAGE_KEY, false)
    const currentSidebarWidth = isSidebarCollapsed ? sidebarCollapsedWidth : sidebarWidth

    // Window state: minimized (FAB only), normal, or expanded (dialog)
    const [minimized, setMinimized] = useState(false)
    const [expanded, setExpanded] = useState(false)

    const chat = useChatAssistant({
        conversationType,
        conversationTitle,
        metadata,
        errorMessageId: 'copilot_conversation_start_error',
    })

    // Handler for suggestion clicks from zero state - sets input text
    const handleSuggestionClick = useCallback((text: string): void => {
        chat.setInput(text)
    }, [chat])

    // Resolve zeroState - call if it's a function, otherwise use as-is
    const resolvedZeroState = typeof zeroState === 'function'
        ? zeroState(handleSuggestionClick)
        : zeroState

    // Auto-start with message if provided (e.g., CSV auto-analyzes columns)
    useEffect(() => {
        if (!hasAutoStarted.current && autoStartMessage && chat.isReady) {
            hasAutoStarted.current = true
            void chat.handleStart(autoStartMessage)
        }
    }, [autoStartMessage, chat])

    // Report errors to parent
    useEffect(() => {
        if (chat.status === 'error' && onError) {
            onError(new Error('Conversation error'))
        }
    }, [chat.status, onError])

    const defaultPlaceholder = intl.formatMessage({
        id: 'copilot_input_placeholder',
        defaultMessage: 'Ask anything...',
    })

    const displayTitle = title ?? conversationTitle
    const showZeroState = chat.messages.length === 0 && !chat.isStarting

    /**
     * Renders a single timeline item (message or A2UI surface).
     */
    const renderTimelineItem = (item: TimelineItem): ReactNode => {
        if (item.type === 'message') {
            const msg = item.data as UIMessage
            return (
                <ChatMessage
                    key={item.id}
                    msg={msg}
                    variant="mini"
                    renderActionItem={renderActionItem}
                    showFeedback={!chat.hasA2UI || chat.surfaces.size === 0}
                />
            )
        }
        const surfaceState = item.data as SurfaceState
        const surfaceId = surfaceState.surface.surfaceId
        const showFeedback = surfaceState.surface.state === SurfaceLifecycleState.COMPLETE
        return (
            <Box key={item.id}>
                <A2UISurfaceRenderer
                    surfaceState={surfaceState}
                    onAction={(actionName, context, sourceComponentId): boolean | void =>
                        chat.handleSurfaceAction(surfaceId, actionName, context, sourceComponentId, onLocalAction)
                    }
                    onDataChange={(path, value): void =>
                        chat.handleSurfaceDataChange(surfaceId, path, value)
                    }
                />
                {showFeedback && chat.conversationId &&
                    <SurfaceFeedback
                        conversationId={chat.conversationId}
                        surfaceId={surfaceId}
                    />
                }
            </Box>
        )
    }

    // Minimized state - show FAB only
    if (minimized) {
        return (
            <Box
                sx={{
                    position: 'fixed',
                    bottom: 24,
                    left: `calc(${currentSidebarWidth} + ${WINDOW_MARGIN}px)`,
                    zIndex: 1300,
                }}
            >
                <Fab
                    size="medium"
                    onClick={(): void => setMinimized(false)}
                    sx={{ boxShadow: 'shadows.default', bgcolor: 'background.paper' }}
                >
                    <AgenticAiIcon fontSize="medium" />
                </Fab>
            </Box>
        )
    }

    // Expanded state - full dialog view
    if (expanded) {
        return (
            <C1Dialog open onClose={(): void => setExpanded(false)} maxWidth="lg" fullWidth>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ px: 3, py: 2, borderBottom: 1, borderColor: 'divider' }}
                >
                    <Stack direction="row" spacing={2} alignItems="center">
                        <AgenticAiIcon sx={{ fontSize: 24, color: 'primary.extraDark' }} />
                        <Typography variant="h4" sx={{ fontWeight: 500 }}>
                            {displayTitle}
                        </Typography>
                        {showBetaChip && <Chip label={intl.formatMessage({ id: 'copilot.beta', defaultMessage: 'Beta' })} size="small" color="info" />}
                        {chat.status === 'thinking' && <CircularProgress size={14} thickness={4} />}
                    </Stack>
                    <Stack direction="row" spacing={1}>
                        <IconButton
                            size="small"
                            onClick={(): void => setExpanded(false)}
                            sx={{ color: 'text.secondary' }}
                            aria-label={intl.formatMessage({ id: 'copilot_shrink', defaultMessage: 'Shrink' })}
                        >
                            <C1Icon icon={Minimize2} sx={{ fontSize: 16 }} />
                        </IconButton>
                        {onClose &&
                            <IconButton
                                size="small"
                                onClick={onClose}
                                sx={{ color: 'text.secondary' }}
                                aria-label={intl.formatMessage({ id: 'copilot_close', defaultMessage: 'Close' })}
                            >
                                <C1Icon icon={X} sx={{ fontSize: 16 }} />
                            </IconButton>
                        }
                    </Stack>
                </Stack>
                <C1DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', height: '70vh' }}>
                    <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                        {showZeroState && resolvedZeroState}
                        {!showZeroState &&
                            <Stack spacing={0} sx={{ px: 2, py: 1 }}>
                                {chat.timelineItems.map(renderTimelineItem)}
                                <div ref={chat.messagesEndRef} />
                            </Stack>
                        }
                    </Box>
                    <Box sx={{ px: 2, pb: 2 }}>
                        <ChatInput
                            variant="mini"
                            input={chat.input}
                            handleInputChange={(e): void => chat.setInput(e.target.value)}
                            handleSubmit={chat.handleSubmit}
                            status={chat.status}
                            placeholder={placeholder ?? defaultPlaceholder}
                        />
                    </Box>
                </C1DialogContent>
            </C1Dialog>
        )
    }

    // Floating variant - fixed position box that respects sidebar width
    return (
        <Box
            sx={{
                position: 'fixed',
                bottom: 24,
                left: `calc(${currentSidebarWidth} + ${WINDOW_MARGIN}px)`,
                width: 450,
                height: 520,
                minWidth: 350,
                minHeight: 300,
                maxWidth: `calc(100vw - ${currentSidebarWidth} - ${WINDOW_MARGIN * 2}px)`,
                maxHeight: 'calc(100vh - 48px)',
                bgcolor: 'background.paper',
                borderRadius: C1BorderRadius.medium,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: shadows.strong,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                zIndex: 1300,
                resize: 'both',
            }}
        >
            {/* Header */}
            <Stack spacing={2} sx={{ p: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 40,
                                height: 40,
                                bgcolor: 'primary.extraLight',
                                borderRadius: C1BorderRadius.small,
                                flexShrink: 0,
                            }}
                        >
                            <AgenticAiIcon sx={{ fontSize: 24, color: 'primary.extraDark' }} />
                        </Box>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Typography variant="h4" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                {displayTitle}
                            </Typography>
                            {showBetaChip && <Chip label={intl.formatMessage({ id: 'copilot.beta', defaultMessage: 'Beta' })} size="small" color="info" />}
                            {chat.status === 'thinking' && <CircularProgress size={14} thickness={4} />}
                        </Stack>
                    </Stack>
                    <Stack direction="row" spacing={1}>
                        <IconButton
                            size="small"
                            onClick={(): void => setMinimized(true)}
                            sx={{ color: 'text.secondary', p: 1 }}
                            aria-label={intl.formatMessage({ id: 'copilot_minimize', defaultMessage: 'Minimize' })}
                        >
                            <C1Icon icon={Minus} sx={{ fontSize: 16 }} />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={(): void => setExpanded(true)}
                            sx={{ color: 'text.secondary', p: 1 }}
                            aria-label={intl.formatMessage({ id: 'copilot_expand', defaultMessage: 'Expand' })}
                        >
                            <C1Icon icon={Maximize2} sx={{ fontSize: 16 }} />
                        </IconButton>
                        {onClose &&
                            <IconButton
                                size="small"
                                onClick={onClose}
                                sx={{ color: 'text.secondary', p: 1 }}
                                aria-label={intl.formatMessage({ id: 'copilot_close', defaultMessage: 'Close' })}
                            >
                                <C1Icon icon={X} sx={{ fontSize: 16 }} />
                            </IconButton>
                        }
                    </Stack>
                </Stack>
            </Stack>
            <Divider sx={{ mx: 2 }} />

            {/* Content */}
            <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                {showZeroState && resolvedZeroState}
                {!showZeroState &&
                    <Stack spacing={0} sx={{ px: 1, py: 1 }}>
                        {chat.timelineItems.map(renderTimelineItem)}
                        <div ref={chat.messagesEndRef} />
                    </Stack>
                }
            </Box>

            {/* Input */}
            <Box sx={{ px: 1, pb: 1 }}>
                <ChatInput
                    variant="mini"
                    input={chat.input}
                    handleInputChange={(e): void => chat.setInput(e.target.value)}
                    handleSubmit={chat.handleSubmit}
                    status={chat.status}
                    placeholder={placeholder ?? defaultPlaceholder}
                />
            </Box>
        </Box>
    )
}

// Re-export types for consumers
export type { CopilotAssistantProps }
